import React, { useEffect, useRef, useState } from 'react';
import { Auth } from 'aws-amplify';
import axios from 'axios';
import { LoadingOutlined, WarningTwoTone } from '@ant-design/icons';
import { Space, Tooltip } from 'antd';

import { warning_color } from '../../colors';
import ValidatedAddress from './ValidatedAddress';

const deliverabilityCache = new Map<string, string | undefined>();
const localApi = process.env.REACT_APP_CHALICE_URL?.includes('localhost');
const checkFailureMessage = localApi
  ? "Couldn't check address deliverability. Is Chalice running on localhost:8000?"
  : "Couldn't check address deliverability. Try again shortly.";
const statusIndicatorStyle: React.CSSProperties = {
  display: 'inline-flex',
  padding: '0 8px',
};

export const checkAddressDeliverability = async (
  address: string
): Promise<string | undefined> => {
  const normalizedAddress = address.trim();
  if (deliverabilityCache.has(normalizedAddress))
    return deliverabilityCache.get(normalizedAddress);

  const token = (await Auth.currentSession()).getIdToken().getJwtToken();
  const response = await axios.post(
    `${process.env.REACT_APP_CHALICE_URL}/check_address_deliverability`,
    { address: normalizedAddress },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  const deliverability = response.data.deliverability as string | undefined;
  deliverabilityCache.set(normalizedAddress, deliverability);
  return deliverability;
};

export const useAddressDeliverability = (
  address: string | null | undefined,
  debounceMs = 600
) => {
  const [deliverability, setDeliverability] = useState<string | undefined>();
  const [checking, setChecking] = useState(false);
  const [failed, setFailed] = useState(false);
  const latestAddress = useRef(address);
  latestAddress.current = address;

  useEffect(() => {
    const normalizedAddress = address?.trim();
    if (!normalizedAddress) {
      setDeliverability(undefined);
      setChecking(false);
      setFailed(false);
      return;
    }

    setChecking(true);
    setFailed(false);
    const timer = window.setTimeout(() => {
      checkAddressDeliverability(normalizedAddress)
        .then((result) => ({ result, failed: false }))
        .catch(() => ({ result: undefined, failed: true }))
        .then((result) => {
          if (latestAddress.current?.trim() !== normalizedAddress) return;
          setDeliverability(result.result);
          setFailed(result.failed);
          setChecking(false);
        });
    }, debounceMs);

    return () => window.clearTimeout(timer);
  }, [address, debounceMs]);

  return { checking, deliverability, failed };
};

const LiveValidatedAddress: React.FC<{
  address?: string | null;
  storedAddress?: string | null;
  storedDeliverability?: string;
  showAddress?: boolean;
}> = ({ address, storedAddress, storedDeliverability, showAddress }) => {
  const matchesStored = (address ?? '').trim() === (storedAddress ?? '').trim();
  const { checking, deliverability, failed } = useAddressDeliverability(
    matchesStored ? undefined : address
  );
  const addressText = showAddress ? address || '—' : undefined;

  if (!address?.trim())
    return addressText ? (
      <span style={statusIndicatorStyle}>{addressText}</span>
    ) : null;

  if (!matchesStored && checking)
    return (
      <Space direction="horizontal" style={statusIndicatorStyle}>
        <Tooltip title="Checking deliverability…">
          <LoadingOutlined aria-label="Checking address deliverability" />
        </Tooltip>
        {addressText}
      </Space>
    );

  if (!matchesStored && failed)
    return (
      <Space direction="horizontal" style={statusIndicatorStyle}>
        <Tooltip title={checkFailureMessage}>
          <WarningTwoTone
            aria-label="Address deliverability check failed"
            twoToneColor={warning_color}
          />
        </Tooltip>
        {addressText}
      </Space>
    );

  return (
    <span style={statusIndicatorStyle}>
      <ValidatedAddress
        address={addressText}
        deliverability={matchesStored ? storedDeliverability : deliverability}
      />
    </span>
  );
};

export default LiveValidatedAddress;
